import { demoVehicleImages } from "@/assets/demo";
import type { VehicleData } from "@/types/vehicle";
import type { MaintenanceRecord } from "@/types/maintenance";
import type { BookingStatus } from "@/types/booking-status";
import { createDefaultLegalTemplates } from "@/lib/legalTemplates";

// ── Customers ──────────────────────────────────────────────────────
export interface DemoCustomer {
  id: string;
  full_name: string;
  phone: string | null;
  email: string | null;
  flags?: string[];
  notes?: string | null;
  created_at?: string | null;
}

export const INITIAL_CUSTOMERS: DemoCustomer[] = [
  { id: "c1", full_name: "Ahmad Hassan", phone: "+961 71 123 456", email: "ahmad@email.com", flags: ["vip"], notes: "Premium client", created_at: "2025-06-15T00:00:00" },
  { id: "c2", full_name: "Sara Khalil", phone: "+961 70 789 012", email: "sara@email.com", flags: [], notes: null, created_at: "2025-08-20T00:00:00" },
  { id: "c3", full_name: "Omar Naser", phone: "+961 76 345 678", email: "omar@email.com", flags: ["late_returns"], notes: null, created_at: "2025-09-10T00:00:00" },
  { id: "c4", full_name: "Lina Farah", phone: "+961 03 901 234", email: "lina@email.com", flags: ["vip"], notes: "Corporate account", created_at: "2025-07-01T00:00:00" },
  { id: "c5", full_name: "Karim Jaber", phone: "+961 71 567 890", email: null, flags: [], notes: null, created_at: "2025-11-05T00:00:00" },
  { id: "c6", full_name: "Nadia Mansour", phone: "+961 78 234 567", email: "nadia@email.com", flags: ["damages"], notes: "Minor scratch incident", created_at: "2025-10-12T00:00:00" },
  { id: "c7", full_name: "Rami Tabbara", phone: "+961 70 111 222", email: null, flags: ["blacklisted"], notes: "Unpaid balance", created_at: "2025-05-20T00:00:00" },
];

// ── Vehicles ───────────────────────────────────────────────────────
export const INITIAL_VEHICLES: VehicleData[] = [
  { id: "v1", vehicle_code: "VEH-001", plate_number: "B 123456", make: "Toyota", model: "Corolla", year: 2023, category: "compact", color: "White", mileage: 32450, daily_rate: 50, status: "rented", insurance_expiry: "2026-06-15", image_url: demoVehicleImages.v1, notes: null, lube_change_interval_km: 5000, last_lube_change_km: 30000, last_lube_change_date: "2026-01-10", tire_change_interval_km: 40000, last_tire_change_km: 20000, last_tire_change_date: null, brake_service_interval_km: 30000, last_brake_service_km: 15000, last_brake_service_date: null, engine_check_interval_km: 50000, last_engine_check_km: 10000, last_engine_check_date: null },
  { id: "v2", vehicle_code: "VEH-002", plate_number: "B 789012", make: "Kia", model: "Sportage", year: 2024, category: "suv", color: "Black", mileage: 12100, daily_rate: 70, status: "available", insurance_expiry: "2026-09-20", image_url: demoVehicleImages.v2, notes: "Premium package", lube_change_interval_km: 5000, last_lube_change_km: 10000, last_lube_change_date: "2026-02-01", tire_change_interval_km: 40000, last_tire_change_km: 0, last_tire_change_date: null, brake_service_interval_km: 30000, last_brake_service_km: 0, last_brake_service_date: null, engine_check_interval_km: 50000, last_engine_check_km: 0, last_engine_check_date: null },
  { id: "v3", vehicle_code: "VEH-003", plate_number: "B 345678", make: "Hyundai", model: "Tucson", year: 2023, category: "suv", color: "Gray", mileage: 28900, daily_rate: 65, status: "rented", insurance_expiry: null, image_url: demoVehicleImages.v3, notes: null, lube_change_interval_km: 5000, last_lube_change_km: 25000, last_lube_change_date: "2025-12-15", tire_change_interval_km: 40000, last_tire_change_km: 0, last_tire_change_date: null, brake_service_interval_km: 30000, last_brake_service_km: 0, last_brake_service_date: null, engine_check_interval_km: 50000, last_engine_check_km: 0, last_engine_check_date: null },
  { id: "v4", vehicle_code: "VEH-004", plate_number: "B 901234", make: "BMW", model: "X3", year: 2024, category: "luxury", color: "Blue", mileage: 8200, daily_rate: 120, status: "rented", insurance_expiry: "2026-12-01", image_url: demoVehicleImages.v4, notes: "VIP vehicle", lube_change_interval_km: 5000, last_lube_change_km: 5000, last_lube_change_date: "2026-01-20", tire_change_interval_km: 40000, last_tire_change_km: 0, last_tire_change_date: null, brake_service_interval_km: 30000, last_brake_service_km: 0, last_brake_service_date: null, engine_check_interval_km: 50000, last_engine_check_km: 0, last_engine_check_date: null },
  { id: "v5", vehicle_code: "VEH-005", plate_number: "B 567890", make: "Honda", model: "Civic", year: 2023, category: "compact", color: "Red", mileage: 41300, daily_rate: 45, status: "available", insurance_expiry: "2026-03-10", image_url: demoVehicleImages.v5, notes: null, lube_change_interval_km: 5000, last_lube_change_km: 40000, last_lube_change_date: "2026-02-10", tire_change_interval_km: 40000, last_tire_change_km: 20000, last_tire_change_date: null, brake_service_interval_km: 30000, last_brake_service_km: 15000, last_brake_service_date: null, engine_check_interval_km: 50000, last_engine_check_km: 0, last_engine_check_date: null },
  { id: "v6", vehicle_code: "VEH-006", plate_number: "B 112233", make: "Mercedes", model: "C-Class", year: 2024, category: "luxury", color: "Silver", mileage: 5600, daily_rate: 130, status: "maintenance", insurance_expiry: "2027-01-15", image_url: demoVehicleImages.v6, notes: "Scheduled maintenance", lube_change_interval_km: 5000, last_lube_change_km: 5000, last_lube_change_date: "2026-02-05", tire_change_interval_km: 40000, last_tire_change_km: 0, last_tire_change_date: null, brake_service_interval_km: 30000, last_brake_service_km: 0, last_brake_service_date: null, engine_check_interval_km: 50000, last_engine_check_km: 0, last_engine_check_date: null },
];

// ── Mini refs used in bookings ─────────────────────────────────────
const CM = (c: DemoCustomer) => ({ id: c.id, full_name: c.full_name, phone: c.phone, email: c.email });
const VM = (v: VehicleData) => ({ id: v.id, make: v.make, model: v.model, plate_number: v.plate_number, daily_rate: v.daily_rate, status: v.status });

// ── Bookings ───────────────────────────────────────────────────────
export interface DemoBooking {
  id: string;
  status: BookingStatus;
  start_date: string;
  end_date: string;
  daily_rate: number | null;
  total_price: number | null;
  notes: string | null;
  pickup_mileage: number | null;
  return_mileage: number | null;
  actual_return_date: string | null;
  signature_url: string | null;
  customer_id: string | null;
  vehicle_id: string | null;
  customer: { id: string; full_name: string; phone: string | null; email: string | null } | null;
  vehicle: { id: string; make: string | null; model: string | null; plate_number: string; daily_rate: number | null; status?: string } | null;
}

export const INITIAL_BOOKINGS: DemoBooking[] = [
  { id: "b1", status: "active", start_date: "2026-02-20T00:00:00", end_date: "2026-02-28T00:00:00", daily_rate: 50, total_price: 400, notes: null, pickup_mileage: 45200, return_mileage: null, actual_return_date: null, signature_url: null, customer_id: "c1", vehicle_id: "v1", customer: CM(INITIAL_CUSTOMERS[0]), vehicle: VM(INITIAL_VEHICLES[0]) },
  { id: "b2", status: "ready", start_date: "2026-02-25T00:00:00", end_date: "2026-03-01T00:00:00", daily_rate: 65, total_price: 260, notes: "Airport pickup", pickup_mileage: null, return_mileage: null, actual_return_date: null, signature_url: null, customer_id: "c2", vehicle_id: "v2", customer: CM(INITIAL_CUSTOMERS[1]), vehicle: VM(INITIAL_VEHICLES[1]) },
  { id: "b3", status: "return_in_progress", start_date: "2026-02-18T00:00:00", end_date: "2026-02-24T00:00:00", daily_rate: 60, total_price: 360, notes: null, pickup_mileage: 32100, return_mileage: 32580, actual_return_date: null, signature_url: null, customer_id: "c3", vehicle_id: "v3", customer: CM(INITIAL_CUSTOMERS[2]), vehicle: VM(INITIAL_VEHICLES[2]) },
  { id: "b4", status: "active", start_date: "2026-02-22T00:00:00", end_date: "2026-03-02T00:00:00", daily_rate: 120, total_price: 960, notes: "VIP client", pickup_mileage: 18400, return_mileage: null, actual_return_date: null, signature_url: null, customer_id: "c4", vehicle_id: "v4", customer: CM(INITIAL_CUSTOMERS[3]), vehicle: VM(INITIAL_VEHICLES[3]) },
  { id: "b5", status: "inquiry", start_date: "2026-03-01T00:00:00", end_date: "2026-03-05T00:00:00", daily_rate: 45, total_price: 180, notes: null, pickup_mileage: null, return_mileage: null, actual_return_date: null, signature_url: null, customer_id: "c5", vehicle_id: "v5", customer: CM(INITIAL_CUSTOMERS[4]), vehicle: VM(INITIAL_VEHICLES[4]) },
  { id: "b6", status: "confirmed", start_date: "2026-02-27T00:00:00", end_date: "2026-03-03T00:00:00", daily_rate: 50, total_price: 200, notes: null, pickup_mileage: null, return_mileage: null, actual_return_date: null, signature_url: null, customer_id: "c1", vehicle_id: "v5", customer: CM(INITIAL_CUSTOMERS[0]), vehicle: VM(INITIAL_VEHICLES[4]) },
  { id: "b7", status: "completed", start_date: "2026-02-10T00:00:00", end_date: "2026-02-15T00:00:00", daily_rate: 65, total_price: 325, notes: "Returned clean", pickup_mileage: 24000, return_mileage: 24800, actual_return_date: "2026-02-15", signature_url: null, customer_id: "c2", vehicle_id: "v2", customer: CM(INITIAL_CUSTOMERS[1]), vehicle: VM(INITIAL_VEHICLES[1]) },
  { id: "b8", status: "cancelled", start_date: "2026-02-12T00:00:00", end_date: "2026-02-14T00:00:00", daily_rate: 60, total_price: 120, notes: "Customer cancelled", pickup_mileage: null, return_mileage: null, actual_return_date: null, signature_url: null, customer_id: "c3", vehicle_id: "v3", customer: CM(INITIAL_CUSTOMERS[2]), vehicle: VM(INITIAL_VEHICLES[2]) },
];

// ── Payments ───────────────────────────────────────────────────────
export interface DemoPayment {
  id: string;
  booking_id: string | null;
  amount: number | null;
  status: string | null;
  payment_type: string | null;
  method: string | null;
  currency: string | null;
  paid_at: string | null;
  created_at: string | null;
  customer_id: string | null;
}

export const INITIAL_PAYMENTS: DemoPayment[] = [
  { id: "p1", booking_id: "b1", amount: 400, status: "paid", payment_type: "rental_fee", method: "card", currency: "USD", paid_at: "2026-02-20T10:00:00", created_at: "2026-02-20T10:00:00", customer_id: "c1" },
  { id: "p2", booking_id: "b1", amount: 200, status: "held", payment_type: "deposit", method: "card", currency: "USD", paid_at: null, created_at: "2026-02-20T10:05:00", customer_id: "c1" },
  { id: "p3", booking_id: "b2", amount: 260, status: "paid", payment_type: "rental_fee", method: "cash", currency: "USD", paid_at: "2026-02-25T09:00:00", created_at: "2026-02-25T09:00:00", customer_id: "c2" },
  { id: "p4", booking_id: "b2", amount: 200, status: "held", payment_type: "deposit", method: "cash", currency: "USD", paid_at: null, created_at: "2026-02-25T09:05:00", customer_id: "c2" },
  { id: "p5", booking_id: "b4", amount: 960, status: "paid", payment_type: "rental_fee", method: "bank_transfer", currency: "USD", paid_at: "2026-02-22T14:00:00", created_at: "2026-02-22T14:00:00", customer_id: "c4" },
  { id: "p6", booking_id: "b4", amount: 300, status: "held", payment_type: "deposit", method: "bank_transfer", currency: "USD", paid_at: null, created_at: "2026-02-22T14:05:00", customer_id: "c4" },
  { id: "p7", booking_id: "b7", amount: 325, status: "paid", payment_type: "rental_fee", method: "card", currency: "USD", paid_at: "2026-02-10T08:00:00", created_at: "2026-02-10T08:00:00", customer_id: "c2" },
  { id: "p8", booking_id: "b7", amount: 200, status: "refunded", payment_type: "deposit", method: "card", currency: "USD", paid_at: "2026-02-15T16:00:00", created_at: "2026-02-10T08:05:00", customer_id: "c2" },
  { id: "p9", booking_id: "b3", amount: 360, status: "paid", payment_type: "rental_fee", method: "cash", currency: "USD", paid_at: "2026-02-18T11:00:00", created_at: "2026-02-18T11:00:00", customer_id: "c3" },
  { id: "p10", booking_id: "b3", amount: 50, status: "paid", payment_type: "cleaning", method: "cash", currency: "USD", paid_at: "2026-02-24T15:00:00", created_at: "2026-02-24T15:00:00", customer_id: "c3" },
  { id: "p11", booking_id: "b3", amount: 30, status: "paid", payment_type: "late_fee", method: "cash", currency: "USD", paid_at: "2026-02-24T15:00:00", created_at: "2026-02-24T15:00:00", customer_id: "c3" },
];

// ── Alerts ─────────────────────────────────────────────────────────
export interface DemoAlert {
  id: string;
  title: string | null;
  message: string | null;
  severity: string | null;
  alert_type: string | null;
  is_read: boolean;
  resolved: boolean;
  created_at: string;
  reference_id: string | null;
  reference_type: string | null;
}

export const INITIAL_ALERTS: DemoAlert[] = [
  { id: "a1", title: "Insurance Expiring", message: "Honda Civic (B 567890) insurance expires on Mar 10, 2026", severity: "warning", alert_type: "insurance_expiry", is_read: false, resolved: false, created_at: "2026-02-26T08:00:00", reference_id: "v5", reference_type: "vehicle" },
  { id: "a2", title: "Overdue Return", message: "Hyundai Tucson (B 345678) was due Feb 24 — still not returned", severity: "critical", alert_type: "late_return", is_read: false, resolved: false, created_at: "2026-02-25T10:00:00", reference_id: "b3", reference_type: "booking" },
  { id: "a3", title: "Maintenance Due", message: "Toyota Corolla (B 123456) oil change overdue by 2,450 km", severity: "warning", alert_type: "maintenance_due", is_read: true, resolved: false, created_at: "2026-02-24T14:00:00", reference_id: "v1", reference_type: "vehicle" },
  { id: "a4", title: "Ready for Pickup", message: "BMW X3 is confirmed and waiting for Karim Jaber to collect", severity: "info", alert_type: "pickup_ready", is_read: false, resolved: false, created_at: "2026-02-27T09:00:00", reference_id: "b5", reference_type: "booking" },
  { id: "a5", title: "Return Due Today", message: "Lina Farah — BMW X3 is scheduled to return today", severity: "warning", alert_type: "return_today", is_read: false, resolved: false, created_at: "2026-03-17T07:00:00", reference_id: "b2", reference_type: "booking" },
  { id: "a6", title: "Overdue Maintenance Task", message: "Body repair on Toyota Corolla was due Mar 10 — escalated", severity: "warning", alert_type: "task_escalation", is_read: false, resolved: false, created_at: "2026-03-15T08:00:00", reference_id: "m1", reference_type: "maintenance" },
];

// ── Maintenance ────────────────────────────────────────────────────
const demoTaskDefaults = { assigned_to: null, acknowledged_at: null, started_at: null, completed_at: null, proof_photos: [] as string[], completion_notes: null, reviewed_by: null, reviewed_at: null, due_date: null, escalated: false, escalated_at: null, assigned_profile: null, title: null, severity: null, issue_type: null, reported_by_name: null, mileage_at_report: null, technician_name: null, technician_phone: null, vendor_garage: null, estimated_downtime: null, booking_id: null };

export const INITIAL_MAINTENANCE: MaintenanceRecord[] = [
  { id: "m1", vehicle_id: "v1", maintenance_type: "oil_change", description: "Regular oil change at 44,000 km", cost: 85, completed: true, auto_generated: false, created_at: "2025-09-01T10:00:00", status: null, priority: null, vehicle: { id: "v1", make: "Toyota", model: "Corolla", plate_number: "B 123456", mileage: 32450 }, ...demoTaskDefaults },
  { id: "m2", vehicle_id: "v1", maintenance_type: "oil_change", description: "Due for next oil change", cost: null, completed: false, auto_generated: true, created_at: "2026-02-20T08:00:00", status: "pending", priority: "high", vehicle: { id: "v1", make: "Toyota", model: "Corolla", plate_number: "B 123456", mileage: 32450 }, ...demoTaskDefaults },
  { id: "m3", vehicle_id: "v2", maintenance_type: "brake_service", description: "Brake pads replacement", cost: 220, completed: false, auto_generated: false, created_at: "2026-02-18T14:00:00", status: "pending", priority: null, vehicle: { id: "v2", make: "Kia", model: "Sportage", plate_number: "B 789012", mileage: 12100 }, ...demoTaskDefaults },
  { id: "m4", vehicle_id: "v3", maintenance_type: "body_repair", description: "Front bumper scratch from return inspection", cost: 350, completed: false, auto_generated: true, created_at: "2026-02-22T09:30:00", status: "pending", priority: null, vehicle: { id: "v3", make: "Hyundai", model: "Tucson", plate_number: "B 345678", mileage: 28900 }, ...demoTaskDefaults, booking_id: "b3" },
  { id: "m5", vehicle_id: "v3", maintenance_type: "tire_rotation", description: "Tire rotation completed", cost: 60, completed: true, auto_generated: false, created_at: "2025-08-01T11:00:00", status: null, priority: null, vehicle: { id: "v3", make: "Hyundai", model: "Tucson", plate_number: "B 345678", mileage: 28900 }, ...demoTaskDefaults },
  { id: "m6", vehicle_id: "v6", maintenance_type: "engine_repair", description: "Scheduled engine service", cost: null, completed: false, auto_generated: false, created_at: "2026-03-10T09:00:00", status: "in_progress", priority: "high", vehicle: { id: "v6", make: "Mercedes", model: "C-Class", plate_number: "B 112233", mileage: 5600 }, ...demoTaskDefaults, due_date: "2026-03-20T18:00:00" },
];

// ── Inspections ────────────────────────────────────────────────────
export interface DemoInspection {
  id: string;
  booking_id: string | null;
  vehicle_id: string | null;
  inspection_type: string;
  mileage: number | null;
  fuel_level: string | null;
  exterior_clean: boolean | null;
  interior_clean: boolean | null;
  photos: any[];
  damage_areas: any[];
  notes: string | null;
  created_at: string;
}

export const INITIAL_INSPECTIONS: DemoInspection[] = [
  { id: "insp1", booking_id: "b1", vehicle_id: "v1", inspection_type: "pre_rental", mileage: 45200, fuel_level: "full", exterior_clean: true, interior_clean: true, photos: [], damage_areas: [], notes: null, created_at: "2026-02-20T09:00:00" },
  { id: "insp2", booking_id: "b3", vehicle_id: "v3", inspection_type: "pre_rental", mileage: 32100, fuel_level: "full", exterior_clean: true, interior_clean: true, photos: [], damage_areas: [], notes: null, created_at: "2026-02-18T09:00:00" },
  { id: "insp3", booking_id: "b7", vehicle_id: "v2", inspection_type: "pre_rental", mileage: 24000, fuel_level: "full", exterior_clean: true, interior_clean: true, photos: [], damage_areas: [], notes: null, created_at: "2026-02-10T07:30:00" },
  { id: "insp4", booking_id: "b7", vehicle_id: "v2", inspection_type: "post_rental", mileage: 24800, fuel_level: "full", exterior_clean: true, interior_clean: true, photos: [], damage_areas: [], notes: "All clear", created_at: "2026-02-15T15:00:00" },
];

// ── Pickup / Return sessions ───────────────────────────────────────
export interface DemoPickupSession {
  id: string;
  booking_id: string | null;
  vehicle_id: string | null;
  status: string | null;
  id_verified: boolean | null;
  license_verified: boolean | null;
  pre_inspection_done: boolean | null;
  car_cleaned: boolean | null;
  fuel_noted: boolean | null;
  insurance_verified: boolean | null;
  keys_available: boolean | null;
  deposit_terms_reviewed: boolean | null;
  customer_acknowledged: boolean | null;
  acknowledgment_method: string | null;
  completed_at: string | null;
  created_at: string;
  [key: string]: any;
}

export interface DemoReturnSession {
  id: string;
  booking_id: string | null;
  vehicle_id: string | null;
  status: string | null;
  return_mileage: number | null;
  return_fuel_level: string | null;
  damage_found: boolean | null;
  damage_notes: string | null;
  extra_km_charge: number | null;
  fuel_penalty: number | null;
  late_fee: number | null;
  damage_charge: number | null;
  cleaning_fee: number | null;
  deposit_resolution: string | null;
  deposit_withheld_amount: number | null;
  deposit_withhold_reason: string | null;
  completed_at: string | null;
  created_at: string;
  [key: string]: any;
}

export const INITIAL_PICKUP_SESSIONS: DemoPickupSession[] = [];
export const INITIAL_RETURN_SESSIONS: DemoReturnSession[] = [
  { id: "rs1", booking_id: "b3", vehicle_id: "v3", status: "in_progress", return_mileage: 32580, return_fuel_level: "3/4", damage_found: false, damage_notes: null, extra_km_charge: 0, fuel_penalty: 0, late_fee: 30, damage_charge: 0, cleaning_fee: 50, deposit_resolution: null, deposit_withheld_amount: null, deposit_withhold_reason: null, completed_at: null, created_at: "2026-02-24T14:00:00" },
];

// ── Quotes ─────────────────────────────────────────────────────────
export interface DemoQuote {
  id: string;
  customer_id: string | null;
  vehicle_id: string | null;
  start_date: string | null;
  end_date: string | null;
  daily_rate: number | null;
  total_price: number | null;
  notes: string | null;
  status: string | null;
  created_at: string;
}

export const INITIAL_QUOTES: DemoQuote[] = [];

// ── CRM Integrations ──────────────────────────────────────────────
export interface DemoCrmIntegration {
  id: string;
  provider: string | null;
  webhook_url: string | null;
  api_key: string | null;
  status: string | null;
  config: any;
}

export const INITIAL_CRM_INTEGRATIONS: DemoCrmIntegration[] = [
  { id: "demo-int-1", provider: "Zapier", webhook_url: "https://hooks.zapier.com/hooks/catch/123/abc", api_key: "demo-api-key-xyz", status: "active", config: { sync_customers: true, sync_bookings: true } },
];

// ── Business ───────────────────────────────────────────────────────
export const DEMO_BUSINESS = {
  id: "demo-business",
  name: "Demo Rental Co.",
  currency: "USD",
  default_deposit: 200,
  cleaning_fee: 50,
  late_fee_hourly_rate: 15,
  late_fee_grace_hours: 1,
  late_fee_daily_max: 100,
  included_km_per_day: 200,
  extra_km_rate: 0.25,
  fuel_penalty_rates: { "1/4": 75, "1/2": 50, "3/4": 25, empty: 100 },
  notification_preferences: {
    notify_late_return: true,
    notify_return_today: true,
    notify_insurance_expiry: true,
    notify_maintenance_due: true,
    notify_pickup_ready: true,
  },
  brand_colors: {},
  legal_templates: createDefaultLegalTemplates(),
  plan_id: "starter",
  status: "active",
  phone: "+1 555 000 0000",
  email: "demo@rentaldemo.com",
  country: "US",
  city: "New York",
  address: "123 Demo Street",
  whatsapp_number: "+15550000000",
  logo_url: null,
  owner_id: null,
  created_at: "2026-01-01T00:00:00",
  updated_at: "2026-01-01T00:00:00",
  trial_ends_at: null,
  paddle_customer_id: null,
  paddle_subscription_id: null,
  whatsapp_addon_enabled: true,
  ai_addon_enabled: true,
};

// ── Branches ───────────────────────────────────────────────────────
export interface DemoBranch {
  id: string;
  business_id: string;
  name: string;
  address: string | null;
  city: string | null;
  country: string | null;
  phone: string | null;
  timezone: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const INITIAL_BRANCHES: DemoBranch[] = [
  { id: "br1", business_id: "demo-business", name: "Main Branch", address: "123 Demo Street", city: "New York", country: "US", phone: "+1 555 000 0001", timezone: "America/New_York", is_active: true, created_at: "2026-01-01T00:00:00", updated_at: "2026-01-01T00:00:00" },
  { id: "br2", business_id: "demo-business", name: "Downtown", address: "456 Park Avenue", city: "New York", country: "US", phone: "+1 555 000 0002", timezone: "America/New_York", is_active: true, created_at: "2026-01-15T00:00:00", updated_at: "2026-01-15T00:00:00" },
  { id: "br3", business_id: "demo-business", name: "Airport", address: "JFK Terminal 4", city: "New York", country: "US", phone: "+1 555 000 0003", timezone: "America/New_York", is_active: true, created_at: "2026-02-01T00:00:00", updated_at: "2026-02-01T00:00:00" },
];
